"use client";

import { useState } from "react";
import { useQuery } from "convex/react";
import { useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatFuelPrice } from "@/lib/format";
import { format, parseISO } from "date-fns";
import {
  Pencil,
  Trash2,
  Fuel,
  Car,
  ParkingCircle,
  MoreHorizontal,
  FileText,
  Link as LinkIcon,
} from "lucide-react";
import { ExpenseDeleteDialog } from "./expense-delete-dialog";
import { ExpenseFormSheet } from "./expense-form-sheet";

interface ExpenseDetailPageProps {
  expenseId: Id<"expenses">;
}

const TYPE_ICONS = {
  fuel: Fuel,
  tolls: Car,
  parking: ParkingCircle,
  other: MoreHorizontal,
};

const TYPE_LABELS = {
  fuel: "Fuel",
  tolls: "Tolls",
  parking: "Parking",
  other: "Other",
};

export function ExpenseDetailPage({ expenseId }: ExpenseDetailPageProps) {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);

  const expense = useQuery(api.expenses.getExpense, { id: expenseId });

  // Loading state
  if (expense === undefined) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4 pb-40">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  // Not found state
  if (!expense) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Expense not found</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const Icon = TYPE_ICONS[expense.type];
  const typeLabel = TYPE_LABELS[expense.type];
  const formattedDate = format(parseISO(expense.date), "EEEE, MMMM d");

  // Calculate rewards for fuel expenses
  const flexReward = expense.type === "fuel" && expense.rewardsPercent
    ? Math.round(expense.amount * expense.rewardsPercent / 100)
    : 0;
  const upsideReward = expense.type === "fuel" && expense.gallons && expense.upsidePerGallon
    ? Math.round(expense.gallons * expense.upsidePerGallon)
    : 0;
  const totalReward = flexReward + upsideReward;
  const netAmount = expense.amount - totalReward;

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-5 pb-40">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="h-8 w-8 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold">{typeLabel}</h1>
            <p className="text-muted-foreground">{formattedDate}</p>
          </div>
        </div>
        <Badge variant={expense.blockId ? "outline" : "secondary"}>
          {expense.blockId ? "Block" : "General"}
        </Badge>
      </div>

      {/* Amount */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="font-medium">Amount</span>
        </div>
        <div className="pl-7 space-y-2">
          {totalReward > 0 ? (
            <>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gross</span>
                <span className="line-through text-muted-foreground">
                  {formatCurrency(expense.amount)}
                </span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Total Rewards</span>
                <span>+{formatCurrency(totalReward)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold text-lg">
                <span>Net Cost</span>
                <span className="text-destructive">-{formatCurrency(netAmount)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span className="text-destructive">-{formatCurrency(expense.amount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Fuel Details */}
      {expense.type === "fuel" && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Fuel className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Fuel Details</span>
            </div>
            <div className="pl-7 space-y-2">
              {expense.gallons && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gallons</span>
                  <span>{expense.gallons.toFixed(3)} gal</span>
                </div>
              )}
              {expense.pricePerGallon && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price/Gallon</span>
                  <span>{formatFuelPrice(expense.pricePerGallon)}</span>
                </div>
              )}
              {expense.rewardsPercent && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Flex Rewards</span>
                  <span className="text-green-600">
                    {expense.rewardsPercent}% (+{formatCurrency(flexReward)})
                  </span>
                </div>
              )}
              {expense.upsidePerGallon && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Upside</span>
                  <span className="text-green-600">
                    ${(expense.upsidePerGallon / 100).toFixed(2)}/gal (+{formatCurrency(upsideReward)})
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Description */}
      {expense.description && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Notes</span>
            </div>
            <p className="pl-7 text-sm text-muted-foreground">{expense.description}</p>
          </div>
        </>
      )}

      {/* Linked Block */}
      {expense.blockId && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Linked Block</span>
            </div>
            <div className="pl-7">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/blocks/${expense.blockId}`)}
              >
                View Block
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Sticky Actions */}
      <div className="fixed bottom-[calc(2.5rem+env(safe-area-inset-bottom))] sm:bottom-0 left-0 right-0 z-10 bg-background/95 backdrop-blur border-t px-4 py-3 sm:pb-safe">
        <div className="flex gap-2 max-w-2xl mx-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => setShowEditSheet(true)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      <ExpenseDeleteDialog
        expenseId={expenseId}
        expenseType={typeLabel}
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open && !expense) navigate(-1);
        }}
      />

      <ExpenseFormSheet
        mode="edit"
        expense={expense}
        open={showEditSheet}
        onOpenChange={setShowEditSheet}
      />
    </div>
  );
}
